import { sendRequest } from "@infrastructure/api/api";
import type ApiResponse from "@core/types/apiResponse";
import type { ErrorResponse } from "@core/types/errorResponse";
import type { DepartmentReportMediaItem, DepartmentReportDocument } from "./types";

/**
 * Finance & Statistics (GENERAL leadership) endpoints — the church's digitized
 * Finance & Statistics paper form plus its companion income views, aggregated up
 * to district and nation. All three back the same GENERAL `department_report`
 * record (meta.department_submission), so comments/media/submit/approve reuse the
 * normal dept-report endpoints.
 *
 *   GET /{scope}/{id}/department/report/finance/statistics       (all scopes)
 *   GET /{scope}/{id}/department/report/finance/income-composite (church + district)
 *   GET /nation/{id}/department/report/finance/income-breakdown  (nation — no composite)
 *
 * `kpi_id` is unique per income source, so the aggregated pivots key columns on it.
 */

// ── Shared leaf shapes ──────────────────────────────────────────────────────

/** A `{ label: "$", value }` money leaf used throughout the income views. */
export type MoneyLeaf = { label: string; value: number | null };

export type DepartmentSubmission = {
  department_report_id: number;
  report_status_id: number | null;
  report_status_name: string | null;
  notes?: string | null;
  submitted_at?: string | null;
  submitted_by?: number | null;
  submitted_by_name?: string | null;
  approved_at?: string | null;
  approved_by?: number | null;
  approved_by_name?: string | null;
  approval_notes?: string | null;
  created_at?: string | null;
  created_by?: number | null;
  updated_at?: string | null;
  updated_by?: number | null;
} | null;

export type LeadershipSourceTemplate = {
  template_id: number;
  template_name: string;
  submitted_by?: number | null;
};

export type FinanceMeta = {
  church_id?: number;
  church_name?: string;
  district_id?: number;
  district_name?: string;
  nation_id?: number;
  nation_name?: string;
  department_id?: number;
  department_name?: string;
  report_tag_id?: number;
  report_tag_name?: string;
  report_tag_since?: string | null;
  report_tag_until?: string | null;
  report_status_id?: number | null;
  report_status_name?: string | null;
  process_results?: unknown;
  department_submission?: DepartmentSubmission;
  media?: DepartmentReportMediaItem[];
  documents?: DepartmentReportDocument[];
  /** Templates that feed this leadership report (finance + membership/operations). */
  source_templates?: LeadershipSourceTemplate[];
};

// ── Income breakdown (Tab 1) ────────────────────────────────────────────────

/** The five aggregatable measures on an income row / *_total object. */
export type IncomeMeasures = {
  total_raised: MoneyLeaf;
  total_to_island_office: MoneyLeaf;
  total_to_district_office: MoneyLeaf;
  total_out: MoneyLeaf;
  total_to_church: MoneyLeaf;
};

export type IncomeBreakdownRow = IncomeMeasures & {
  kpi_id: number;
  source: string;
  calculation_basis?: string | null;
  category_name?: string | null;
  rule_name?: string | null;
};

export type IncomeBreakdownChurch = {
  church_id: number;
  church_name: string;
  rows: IncomeBreakdownRow[];
  church_total: IncomeMeasures;
};

export type IncomeBreakdownDistrict = {
  district_id: number;
  district_name: string;
  churches: IncomeBreakdownChurch[];
  district_total: IncomeMeasures;
};

// ── District In-House memo (Tab 2) ──────────────────────────────────────────

export type DistrictInHouseRow = {
  kpi_id: number;
  source: string;
  funds_to_island_office: MoneyLeaf;
  funds_to_district_office: MoneyLeaf;
};

export type DistrictInHouseSummaryItem = { label: string; value: MoneyLeaf };

export type DistrictInHouseBlock = {
  rows: DistrictInHouseRow[];
  total_funds_sent_to_district_overseer: {
    funds_to_island_office: MoneyLeaf;
    funds_to_district_office: MoneyLeaf;
  };
  summary: DistrictInHouseSummaryItem[];
};

export type DistrictInHouseChurch = DistrictInHouseBlock & {
  church_id: number;
  church_name: string;
};

// ── Income composite / breakdown response envelopes ─────────────────────────

export type ChurchIncomeCompositeReport = {
  meta: FinanceMeta;
  income_breakdown: { rows: IncomeBreakdownRow[] };
  district_in_house_report: DistrictInHouseBlock;
};

export type DistrictIncomeCompositeReport = {
  meta: FinanceMeta;
  income_breakdown: { churches: IncomeBreakdownChurch[]; grand_total: IncomeMeasures };
  district_in_house_report: {
    churches: DistrictInHouseChurch[];
    aggregate: DistrictInHouseBlock;
  };
};

export type NationIncomeBreakdownReport = {
  meta: FinanceMeta;
  income_breakdown: { districts: IncomeBreakdownDistrict[]; grand_total: IncomeMeasures };
};

// ── Statistics (Tab 3) ──────────────────────────────────────────────────────

/** Template-tagged value leaf used by the statistics sections. */
export type StatLeaf = {
  template_id?: number;
  template_name?: string;
  kpi_id?: number;
  kpi_name?: string;
  label: string;
  value?: string | number | boolean | null;
};

/** A general-information entry: a `$` (dollar) leaf and optionally a total_attendance leaf. */
export type GeneralInformationEntry = {
  label: string;
  dollar?: StatLeaf;
  total_attendance?: StatLeaf;
};

export type GeneralInformation = Record<string, GeneralInformationEntry>;

export type RowsSection = { rows: StatLeaf[] };

/** A nation-office-finances row: template/KPI tags, a label, and a `$` dollar leaf. */
export type NationOfficeFinanceRow = {
  template_id?: number;
  template_name?: string;
  kpi_id?: number;
  kpi_name?: string;
  label: string;
  dollar: MoneyLeaf;
};

export type NationOfficeFinances = {
  rows: NationOfficeFinanceRow[];
  total: { label: string; dollar: MoneyLeaf };
};

/** The four sections of one church's Finance & Statistics sheet. */
export type FinanceStatisticsSections = {
  general_information: GeneralInformation;
  monthly_membership_analysis: RowsSection;
  nation_office_finances: NationOfficeFinances;
  operational_activities_checklist: RowsSection;
};

/** The three aggregated sections (no operational checklist). */
export type FinanceStatisticsAggregate = {
  general_information: GeneralInformation;
  monthly_membership_analysis: RowsSection;
  nation_office_finances: NationOfficeFinances;
};

export type ChurchFinanceStatisticsReport = { meta: FinanceMeta } & FinanceStatisticsSections;

export type FinanceStatisticsChurchEntry = FinanceStatisticsSections & {
  church_id: number;
  church_name: string;
  department_submission: DepartmentSubmission;
  media: DepartmentReportMediaItem[];
};

export type DistrictFinanceStatisticsReport = {
  meta: FinanceMeta;
  churches: FinanceStatisticsChurchEntry[];
  aggregated: FinanceStatisticsAggregate;
};

export type NationFinanceStatisticsDistrictEntry = {
  district_id: number;
  district_name: string;
  department_submission?: DepartmentSubmission;
  churches: FinanceStatisticsChurchEntry[];
  aggregated: FinanceStatisticsAggregate;
};

export type NationFinanceStatisticsReport = {
  meta: FinanceMeta;
  districts: NationFinanceStatisticsDistrictEntry[];
  aggregated: FinanceStatisticsAggregate;
};

// ── Fetchers ────────────────────────────────────────────────────────────────

export type FinanceScope = "church" | "district" | "nation";

function scopePath(scope: FinanceScope, id: number): string {
  if (scope === "church") return `/church/${id}`;
  if (scope === "district") return `/district/${id}`;
  return `/nation/${id}`;
}

function withTag(path: string, reportTagId: number): string {
  return `${path}?${new URLSearchParams({ reportTagId: String(reportTagId) }).toString()}`;
}

/** Finance & Statistics sheet (Tab 3) — all scopes. */
export async function fetchFinanceStatisticsAsync<T>(
  scope: FinanceScope,
  id: number,
  reportTagId: number
): Promise<ApiResponse<T, ErrorResponse>> {
  const url = withTag(`${scopePath(scope, id)}/department/report/finance/statistics`, reportTagId);
  return sendRequest<any, T>("GET", url);
}

/** Income composite (Tabs 1 & 2) — church + district only. */
export async function fetchFinanceCompositeAsync<T>(
  scope: "church" | "district",
  id: number,
  reportTagId: number
): Promise<ApiResponse<T, ErrorResponse>> {
  const url = withTag(`${scopePath(scope, id)}/department/report/finance/income-composite`, reportTagId);
  return sendRequest<any, T>("GET", url);
}

/** Income breakdown (Tab 1 at nation, which has no composite). */
export async function fetchFinanceIncomeBreakdownAsync<T>(
  id: number,
  reportTagId: number
): Promise<ApiResponse<T, ErrorResponse>> {
  const url = withTag(`${scopePath("nation", id)}/department/report/finance/income-breakdown`, reportTagId);
  return sendRequest<any, T>("GET", url);
}
